import {  useEffect, useMemo,  useRef,  useState } from "react";
 
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { convertToZonedTimestamp  } from "common/helpers/dateAndTimeHelpersV2";
import { ButtonCancel,  ButtonPrimary, CalendarButton } from 'common/ui';
import { useTranslation } from "react-i18next";
import { DatePickerDefaultSideColumn, Calendar, DateRangeProps, CalendarProps } from "./";
 
import { DatePickerButtonContainer, RangeItemContainer } from "./DatePickerButton.elements";
import useClickOutside from "hooks/useClickOutside";
import useEscapeKey from "common/hooks/useEscapeKey";
import { ToggleSection } from "common/ui/ToggleSection/ToggleSection";
import { IcoChevronDown } from "icons/indexed-icons/IcoChevronDown";
 
 

export const DatePickerButton = ({ 
  monthsLimit = 12, 
  hasFutureDates = false, 
  dateOnly = false,
  timeRange = true,
  style, 
  startTime, 
  endTime, 
  timeZone, 
  selectGroups,
  Components,
  handleSubmit,
  maxStartDate
}: CalendarProps): JSX.Element => {

  const ColRight = DatePickerDefaultSideColumn
  const { t } = useTranslation(['mh'])

   const [ranges, setRanges] = useState<DateRangeProps>({
    startTime,
    endTime
  })

  const containerRef = useRef(null)
  const containerEl = containerRef?.current as HTMLDivElement
  
  const returnType = useMemo(() => {
    const x = startTime || endTime
    if (typeof x === 'number') return { number: true }
    if (typeof x === 'string') return { string: true }
    if (startTime instanceof Date) return { date: true }    
  },[startTime, endTime])

  const handleClose = () => {
    if (containerEl) {
      containerEl.querySelector('.dropdown-area')?.classList.remove('is-open')
      containerEl.dataset.open = 'false'
    }
  }

  const handleToggle = () => {  
    if (containerEl) {
      containerEl.querySelector('.dropdown-area')?.classList.toggle('is-open')
      containerEl.dataset.open = containerEl.querySelector('.dropdown-area')?.classList.contains('is-open') ? 'true' : 'false'
    }
  }

  const submitHandler = (inRange) =>  {
 
    if (!inRange?.startTime || !inRange?.endTime) return

    let retRange = {
      startTime: inRange?.startTime,
      endTime: inRange?.endTime
    }

    if (returnType?.date) retRange = {
      startTime: new Date(inRange?.startTime),
      endTime: new Date(inRange?.endTime)
    }

    if (returnType?.string) retRange = {
      startTime: convertToZonedTimestamp(inRange?.startTime, timeZone) as string,
      endTime: convertToZonedTimestamp(inRange?.endTime, timeZone) as string
    }
   
    handleSubmit?.({...ranges, ...retRange})
    handleClose()
  }

 
 
  useEffect(() => {
    setRanges({startTime, endTime})
  }, [startTime, endTime])
  
  useClickOutside(containerRef, () => handleClose())
  useEscapeKey(handleClose)

  let gridCols = `1fr`
  if (Components?.ColLeft) gridCols = `auto ${gridCols}`
  if (typeof ColRight === 'function') gridCols = `${gridCols} auto`

  const displayFormat = dateOnly ? 'date' : 'full'

  if (maxStartDate) monthsLimit = undefined
  
  return (
    <DatePickerButtonContainer className="date-picker-button-v2" ref={containerRef} {...{gridCols, style}}> 
      <CalendarButton handleClick={handleToggle}>
        {convertToZonedTimestamp(startTime as string, timeZone, displayFormat)} - {convertToZonedTimestamp(endTime as string, timeZone, displayFormat)}
      </CalendarButton>
      <div className='dropdown-area'>
  
        <div className='scrollable-area'>
          <div className='main-calendar-area'>
            <Calendar {...{ ...ranges, maxStartDate, timeZone, handleChange: (range) => setRanges?.(range), monthsLimit, hasFutureDates }} />
            <div className='current-range-row'>
              <div className='label'>{t('start')}</div>
              <div className='label'>{t('end')}</div>
              <CalendarButton className='full-width date-display'>
                {ranges?.startTime && convertToZonedTimestamp(ranges.startTime, timeZone, 'date')}
              </CalendarButton>
              <CalendarButton className='full-width date-display'>
                {ranges?.endTime && convertToZonedTimestamp(ranges.endTime, timeZone, 'date')}
              </CalendarButton>
            </div>
            {timeRange && <TimeToggler {...{ranges, setRanges, timeZone}} />}
          </div>
          {ColRight && 
          <div className='col-right'>
            <ColRight {...{
              selected: ranges?.label,
              timeZone, 
              setDateRange: (x) => {
                setRanges?.(x)
              },
              startTime, 
              endTime, 
              selectGroups,
              Components
            }} />
          </div>}
         </div>

        <div className='submit-btn-row'>
          <ButtonCancel onClick={() => handleClose()}>
            Cancel
          </ButtonCancel>
          <ButtonPrimary className="date-submit-btn" onClick={() => submitHandler(ranges)}>
            Apply
          </ButtonPrimary>
        </div>
      </div>
               
    </DatePickerButtonContainer>
     
    
   );
};


const TimeToggler = ({ ranges, setRanges, timeZone}: { timeZone?: string, ranges?: DateRangeProps, setRanges: (x?: DateRangeProps) => void}) => {
   
  if (!ranges?.startTime || !ranges?.endTime || !timeZone || !setRanges) return <></>

  const RangeItem = ({start}: { start?: boolean}) => {
    const time = (start ? ranges?.startTime : ranges?.endTime) as number
    const [h, m, a] = convertToZonedTimestamp(time, timeZone, 'H:mm:a').split(':')

    let disabled = false

    if (start) {
      if (ranges?.startTime > ranges?.endTime) disabled = true
      else disabled = false
    } else {
      if (ranges?.endTime < ranges?.startTime) disabled = true
      else disabled = false
    }

    //const ampm = { [a]: true }
    
    // Convert 24-hour format to 12-hour format for display
    const hour24 = Number(h)
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    
    // Helper functions to toggle AM/PM and shift the day by 12 hours
    const toggleAmPmUp = () => {

      if(start){
        if(((time as number) + (12 * 60 * 60 * 1000)) > (ranges?.endTime as number)) return (time as number)
      }

      return (time as number) + (12 * 60 * 60 * 1000)
    }

    const toggleAmPmDown = () => {
    
      if(!start){
        if(((time as number) - (12 * 60 * 60 * 1000)) < (ranges?.startTime as number)) return (time as number)
      }

      return (time as number) - (12 * 60 * 60 * 1000)
    }

    const toggleDown = (minutes = 1) => {

      if(!start){
        const newEndTime = time - (60000 * minutes)
        if(newEndTime < (ranges?.startTime as number)) return
      }


      setRanges(start ? {...ranges, startTime: time - (60000 * minutes)} : {...ranges, endTime: time - (60000 * minutes)})
    }

    const toggleUp = (minutes = 1) => {

      if(start){
        const newStartTime = time + (60000 * minutes)
        if(newStartTime > (ranges?.endTime as number)) return
      }

      setRanges(start ? {...ranges, startTime: time + (60000 * minutes)} : {...ranges, endTime: time + (60000 * minutes)})
    }
   

    return (

      <RangeItemContainer className={`time-group ${disabled ? 'disabled' : ''}`.trim()}>

        <button className='up' onClick={() => toggleUp(60)}><IcoChevronDown /></button>
        <div className='spacer'></div>
        <button className='up' onClick={() => toggleUp()}><IcoChevronDown /></button>
        <div className='spacer'></div>
        <button
          className='up'
          onClick={() => {
            const next = toggleAmPmUp()
            if (next <= Date.now()) {
              setRanges(start ? { ...ranges, startTime: next } : { ...ranges, endTime: next })
            }
          }}
        >
          <IcoChevronDown />
        </button>

        <div className='value hour'>{hour12 < 10 ? `0${hour12}` : hour12}</div>
        <div className='spacer'>:</div>
        <div className='value minute'>{m}</div>
        <div className='spacer'></div>
        <div className='value ampm'>{a}</div>

        <button className='down' onClick={() => toggleDown(60)}><IcoChevronDown /></button>
        <div className='spacer'></div>
        <button className='down' onClick={() => toggleDown()}><IcoChevronDown /></button>
        <div className='spacer'></div>
        <button className='down' onClick={() => setRanges(start ? {...ranges, startTime: toggleAmPmDown()} : {...ranges, endTime: toggleAmPmDown()})}><IcoChevronDown /></button>

      </RangeItemContainer>
    
    )
  }
  return (!ranges?.startTime || !ranges?.endTime || !timeZone) ? <></> : 
    <ToggleSection title='Add time selection'>
      <RangeItem start />  <RangeItem />
    </ToggleSection>
  
}

 
export default DatePickerButton
